import { css } from '@emotion/react';
import { Button, Divider, Page } from '@frontend/generic-dialpad-accessories';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { CallInformation } from '../components/call-information';
import { PageHeaderWraps } from '../components/generic/page-header-wraps';
import { HeldCall } from '../components/hold/held-call';
import { SettingsRow } from '../components/settings-row';
import { CallDuration } from '../hooks/use-call-duration';
import { useSoftphoneCallState } from '../providers/softphone-call-state-provider';
import { useSoftphoneCallActions } from '../providers/softphone-call-state-provider/softphone-call-actions.provider';
import { useSoftphoneRouter } from '../providers/softphone-router-provider';
import { isEstablishedCall, isOutgoingCall } from '../types';

export const PageMergedHomeMobile = () => {
  const { t } = useTranslation('softphone');
  const mergedCallGroup = useSoftphoneCallState((ctx) => ctx.mergedCallGroup);
  const isMuted = useSoftphoneCallState((ctx) => ctx.isMuted);
  const toggleMute = useSoftphoneCallActions((ctx) => ctx.toggleMute);
  const endCall = useSoftphoneCallActions((ctx) => ctx.endCall);
  const route = useSoftphoneRouter((ctx) => ctx.setGroupCallRoute);
  const toggleHoldMergedCalls = useSoftphoneCallActions((ctx) => ctx.toggleHoldMergedCalls);
  const callOnHold = mergedCallGroup?.some((call) => !!call.onHoldSince);
  const primaryCall = useSoftphoneCallState((ctx) => ctx.primaryCall);
  const establishedCalls = useSoftphoneCallState((ctx) => ctx.establishedCalls);
  const mergedCallIds = useSoftphoneCallState((ctx) => ctx.mergedCallIds);

  const firstMergedCall = mergedCallGroup?.reduce((first, call) => {
    if (!first) {
      return call;
    }
    return isEstablishedCall(call) && isEstablishedCall(first) && call.establishedAt < first.establishedAt
      ? call
      : first;
  }, mergedCallGroup[0]);
  const callWithTimer = firstMergedCall ? firstMergedCall : primaryCall;
  const startTime = callWithTimer
    ? isOutgoingCall(callWithTimer)
      ? callWithTimer.placedAt
      : isEstablishedCall(callWithTimer)
      ? callWithTimer?.establishedAt
      : undefined
    : undefined;

  const endMergedCall = async () => {
    if (mergedCallGroup?.length) {
      const calls = mergedCallGroup.filter(isEstablishedCall);
      for (const call of calls) {
        await endCall(call);
      }
    }
  };

  const holdMergeCals = () => {
    if (mergedCallGroup?.length) {
      const isHolding = mergedCallGroup?.some((call) => !!call.onHoldSince);
      toggleHoldMergedCalls(!isHolding);
    }
  };

  const otherHeldCalls = establishedCalls.filter(
    (call) => !!call.onHoldSince && primaryCall?.id !== call.id && !mergedCallIds?.includes(call.id)
  );

  return (
    <Page>
      <PageHeaderWraps hasBackButton={false}></PageHeaderWraps>
      <Page.Body>
        {mergedCallGroup?.map((call) => {
          return <CallInformation textAlign='center' {...call.remoteParty} hideSubtitle hideCallDuration />;
        })}
        <Text color='white' textAlign='center'>
          <CallDuration connectedAt={startTime} />
        </Text>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            margin: ${theme.spacing(2)} 0;
          `}
        >
          <div
            css={css`
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: ${theme.spacing(2)} ${theme.spacing(3)};
              justify-content: center;

              div:last-child {
                margin: auto;
                grid-column: span 2; /* Span two columns (centered) */
              }
            `}
          >
            <Button.Round
              trackingId='softphone-multicall-managecall'
              onClick={() => route('calls-list')}
              iconName={'user'}
              label={t('Manage')}
            />
            <Button.Round onClick={() => route('keypad')} iconName={'dial'} label={t('Keypad')} />
            <Button.Round
              onClick={(e) => {
                e.stopPropagation();
                toggleMute(!isMuted);
              }}
              selected={isMuted}
              iconName={isMuted ? 'mute' : 'microphone'}
              label={t('Mute')}
              color={isMuted ? 'inverted' : 'default'}
            ></Button.Round>
            <Button.Round
              onClick={(e) => {
                e.stopPropagation();
                holdMergeCals();
              }}
              selected={!!callOnHold}
              iconName={'pause'}
              label={t('Hold')}
              color={!!callOnHold ? 'inverted' : 'default'}
            />
            <Button.Round
              onClick={(e) => {
                e.stopPropagation();
                endMergedCall();
              }}
              color='red'
              iconName={'phone-hangup'}
            ></Button.Round>
          </div>
        </div>
      </Page.Body>
      <Page.Footer>
        {!!otherHeldCalls.length && (
          <>
            <Divider />
            <Text
              color='white'
              css={css`
                margin-bottom: ${theme.spacing(1)};
              `}
            >
              {t('On Local Hold')}
            </Text>
            {otherHeldCalls.map((call) => (
              <HeldCall key={call.id} call={call}></HeldCall>
            ))}
          </>
        )}
        <Divider />
        <SettingsRow />
      </Page.Footer>
    </Page>
  );
};
